import {
  Button,
  ButtonIcon,
  Caption,
  ColorKeys,
  css,
  EllipsisText,
  FormField,
  Heading,
  hex2rgba,
  Inline,
  Modal,
  Paragraph,
  SnapshotIcon,
  Stack,
  StatusDot,
  styled,
  TextInput,
  TrashIcon,
} from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from 'formik';
import { createCacheKey, ENDPOINTS, useActions, useCreatePayload } from 'hammock';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import * as Yup from 'yup';

import { Snapshot } from '@candu-ai/snapshots/types';
import { queryClient } from 'commons/fleet/queryClient';
import { Flags, useFlags } from 'commons/hooks';
import { checkSnapshotHasDocuments } from 'craft/containers/Toolbox/tabs/Placements/utils';
import TimeAgo from 'dashboard/components/TimeAgo';

const validationSchema = Yup.object().shape({
  url: Yup.string()
    .required()
    .matches(/^(https?:\/\/)?.*\..+(\..+)*/, 'Enter a valid URL'),
});

const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    gap: ${theme.spaces.xs};
  `}
`;

const FlexCenter = styled.div`
  display: flex;
  justify-content: center;
`;

const SnapshotListWrapper = styled.div`
  ${({ theme }) => css`
    max-height: 50vh;
    width: 980px;
    overflow-y: auto;
    padding-bottom: ${theme.spaces.l};
    margin-top: ${theme.spaces.s};
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(233px, max-content));
    grid-gap: ${theme.spaces.s};
    justify-content: center;
  `}
`;

const SnapshotListItem = styled.div<{ isSelected: boolean }>`
  ${({ theme, isSelected }) => css`
    width: 233px;
    height: 233px;
    border: ${isSelected ? '2px' : '1px'} solid
      ${isSelected ? hex2rgba(theme.colors.BrandPurple, 0.5) : theme.colors.GlitterGrayD10};
    border-radius: ${theme.borderRadius.small};
    position: relative;

    ${isSelected &&
    css`
      &:after {
        content: 'Current Snapshot';
        font-size: ${theme.fonts.small.fontSize};
        color: ${theme.colors.White};
        background-color: ${theme.colors.BrandPurple};
        position: absolute;
        top: -1px;
        left: -1px;
        border-radius: ${theme.borderRadius.small} 0 ${theme.borderRadius.small} 0;
        padding: ${theme.spaces.xxs} ${theme.spaces.xs};
      }
    `}
  `}
`;

const SnapshotItemThumbnail = styled.div<{ src?: string }>`
  ${({ src = '' }) => css`
    width: 100%;
    height: 175px;
    background-image: url(${src});
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  `}
`;

const SnapshotItemText = styled.div`
  ${({ theme }) => css`
    padding: 0 ${theme.spaces.s};
    display: flex;
    align-items: center;
    justify-content: center;
  `}
`;

const SnapshotItemUrl = styled(Paragraph)`
  ${({ theme }) => css`
    margin: 0;
    color: ${theme.colors.OnyxGray};
    font-weight: ${theme.fontWeights.bold};
    text-align: center;
  `}
`;

const SnapshotItemOverlay = styled.div`
  ${({ theme }) => css`
    display: none;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    align-items: center;
    justify-content: center;
    background-color: ${hex2rgba(theme.colors.OnyxGray, 0.5)};
    // we want to be able to hover over the url text to see the tooltip when it gets ellipsis
    pointer-events: none;

    ${SnapshotListItem}:hover & {
      display: flex;
    }
  `}
`;

const SnapshotItemCTA = styled.div`
  pointer-events: all;
`;

const SnapshotDelete = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: all;
`;

const ModalSeparator = styled(Paragraph)`
  ${({ theme }) => css`
    width: 100%;
    max-width: none;
    text-align: center;
    border-top: 1px solid ${theme.colors.GlitterGrayD10};
    padding-top: ${theme.spaces.s};
  `}
`;

interface Props {
  onClose: VoidFunction;
  onSnapshotChoose: (snapshot: Snapshot) => void | Promise<void>;
  onSnapshotCreate: (snapshotUrl: string) => void;
  onDelete?: (snapshotId: string) => void;
  snapshots: Snapshot[];
  selectedSnapshot?: Snapshot;
  id?: string;
}

export const SnapshotListModal = ({
  onClose,
  onSnapshotChoose,
  onSnapshotCreate,
  snapshots,
  selectedSnapshot,
  id,
  onDelete,
}: Props) => {
  const params = useParams();
  const { pathname } = useLocation();
  const contentId = id || (params.contentId as string);
  const { [Flags.EXISTING_SNAPSHOTS]: withSnapshotChooser } = useFlags();
  const { action } = useActions(ENDPOINTS.snapshots);
  const { getAll } = useCreatePayload(ENDPOINTS.snapshots);
  const [loadingSnapshot, setLoadingSnapshot] = useState(false);

  const isInEditor = useMemo(() => pathname.includes(`editor/${contentId}`), [pathname, contentId]);

  const uniqueSnapshotList = snapshots
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    .reduce((acc: Snapshot[], snapshot) => {
      const originalSnapshot = acc.find(
        (uniqueSnapshot) => uniqueSnapshot.lgS3Url === snapshot.lgS3Url,
      );
      const isSelectedSnapshot = snapshot.id === selectedSnapshot?.id;

      if (!isSelectedSnapshot && (!checkSnapshotHasDocuments(snapshot) || !!originalSnapshot)) {
        // if this is not a valid snapshot or a duplicate, don't include it in the list (unless it is the selected snapshot)
        return acc;
      }

      if (isSelectedSnapshot && !!originalSnapshot) {
        // if our selected snapshot was cloned from an original, assign the original's created date and filter it out from the list
        return [
          ...acc,
          {
            ...snapshot,
            createdAt: originalSnapshot.createdAt,
          },
        ].filter((s) => s.id !== originalSnapshot.id);
      }

      return [...acc, snapshot];
    }, [])
    .sort((a, b) => {
      if (a.id === selectedSnapshot?.id) {
        // always show selected snapshot first on the list
        return -1;
      }
      if (b.id === selectedSnapshot?.id) {
        // always show selected snapshot first on the list
        return 1;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const onChooseSnapshot = useCallback(
    (snapshot: Snapshot) => {
      setLoadingSnapshot(true);

      const clonedSnapshot = {
        ...snapshot,
        contentId,
        id: nanoid(6),
      };

      action({
        name: 'create',
        body: clonedSnapshot,
        invalidates: {
          [ENDPOINTS.snapshots]: {
            query: { finder: 'byContentId', id: contentId },
          },
        },
      })
        .then(async () => {
          // invalidates getAll snapshot action
          await queryClient.invalidateQueries(createCacheKey(getAll({})));

          // we might have async actions happening after a snapshot is chosen,
          // wait for those to finish before we stop showing the loading state
          await onSnapshotChoose(clonedSnapshot);
          setLoadingSnapshot(false);
        })
        .catch(() => {
          setLoadingSnapshot(false);
        });
    },
    [action, contentId, pathname],
  );

  if (!withSnapshotChooser) {
    return null;
  }

  return (
    <Modal onClose={onClose} modalWidth="large">
      <Modal.Body>
        <FlexCenter>
          <Heading size="S" align="center" mt="normal" mb="s">
            Select Snapshot
          </Heading>
        </FlexCenter>

        <FlexCenter>
          <SnapshotListWrapper>
            {uniqueSnapshotList.map((snapshot) => {
              let dateColor: ColorKeys = 'Success';
              if (moment(snapshot.createdAt) < moment().subtract(5, 'weeks')) {
                dateColor = 'Danger';
              }

              return (
                <SnapshotListItem
                  key={snapshot.id}
                  isSelected={selectedSnapshot?.id === snapshot.id}
                >
                  <SnapshotItemThumbnail src={snapshot.thumbnailLgS3Url} />
                  <Stack space="none" css={{ height: '55px' }}>
                    <SnapshotItemText>
                      <EllipsisText content={snapshot.pageUrl} placement="top">
                        <SnapshotItemUrl>{snapshot.pageUrl}</SnapshotItemUrl>
                      </EllipsisText>
                    </SnapshotItemText>
                    <Inline space="xxs" align="baseline" justify="center">
                      <StatusDot color={dateColor} />
                      <Caption mb="none" color="OnyxGrayL20">
                        Taken <TimeAgo date={snapshot.createdAt} />
                      </Caption>
                    </Inline>
                  </Stack>
                  <SnapshotItemOverlay>
                    {isInEditor &&
                      onDelete &&
                      selectedSnapshot?.id !== snapshot.id &&
                      snapshot.contentId === contentId && (
                        <SnapshotDelete>
                          <ButtonIcon
                            icon={TrashIcon}
                            color="white"
                            size="s"
                            onClick={() => onDelete(snapshot.id)}
                          />
                        </SnapshotDelete>
                      )}
                    <SnapshotItemCTA>
                      <Button
                        variant="secondary"
                        onClick={() => onChooseSnapshot(snapshot)}
                        loading={loadingSnapshot}
                        disabled={selectedSnapshot?.id === snapshot.id}
                      >
                        Use Snapshot
                      </Button>
                    </SnapshotItemCTA>
                  </SnapshotItemOverlay>
                </SnapshotListItem>
              );
            })}
          </SnapshotListWrapper>
        </FlexCenter>

        <Stack justify="center" space="s">
          <ModalSeparator size="short" mt="s" mb="s" color="OnyxGrayL30">
            OR
          </ModalSeparator>

          <Formik<{
            url: string;
          }>
            validateOnMount
            initialValues={{
              url: '',
            }}
            validationSchema={validationSchema}
            onSubmit={({ url }) => onSnapshotCreate(url)}
          >
            {({ isSubmitting, isValid, handleSubmit }) => (
              <Form
                data-cy="retake-snapshot-form"
                onKeyDown={(e: KeyboardEvent) => {
                  if (isValid && e.key === 'Enter') {
                    handleSubmit();
                  }
                }}
                css={{ width: '80%', maxWidth: '645px' }}
              >
                <FormRow>
                  <Field name="url">
                    {({
                      field,
                      meta,
                    }: {
                      field: FieldInputProps<string>;
                      meta: FieldMetaProps<string>;
                    }) => (
                      <FormField
                        feedback={meta.touched ? meta.error : ''}
                        state={meta.touched && meta.error ? 'danger' : 'none'}
                      >
                        <TextInput {...field} {...meta} placeholder="your-app.io/dashboard" />
                      </FormField>
                    )}
                  </Field>

                  <Button
                    variant="secondary"
                    icon={SnapshotIcon}
                    loading={isSubmitting}
                    disabled={!isValid}
                    mt="xs"
                    onClick={() => {
                      handleSubmit();
                    }}
                  >
                    Add New Snapshot
                  </Button>
                </FormRow>
              </Form>
            )}
          </Formik>
        </Stack>
      </Modal.Body>
    </Modal>
  );
};
